import { DialogContent, DialogTitle } from '@mui/material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import {
  SuspenseWithSentryErrorBoundary,
  withDialogSuspenseErrorBoundary,
} from '@retail/components';
import { PriceTimeVariant } from '@retail/purchase-agreements/types';
import { Dialog } from '@shared/components';
import { useCallback, useMemo, useState } from 'react';
import { PriceListFilters } from '@retail/purchase-agreements/components';
import { PriceListTableContainer } from './PriceListTableContainer';
import { useAgreements } from '@retail/purchase-agreements/context';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useFilters } from '@retail/products/hooks';
import { useFilterOptions } from '@retail/products/context';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultAgreementId?: number;
  levelProductGroupNr?: string;
  levelMgItemNr?: number;
  supplierNumber: number;
}

function PriceComparisonDialog({
  open,
  onClose,
  defaultAgreementId,
  levelProductGroupNr,
  levelMgItemNr,
  supplierNumber,
}: Props) {
  const t = useCalculusTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const { agreements } = useAgreements({ supplierNumber });

  const [selectedAgreementIds, selectAgreementIds] = useState<number[]>(
    defaultAgreementId ? [defaultAgreementId] : []
  );
  const [checkedPriceType, checkPriceType] = useState<PriceTimeVariant>('CURRENT');

  const handleSelectAgreementId = useCallback(
    (agreementId: number) =>
      selectedAgreementIds.includes(agreementId)
        ? selectAgreementIds(selectedAgreementIds.filter((id) => id !== agreementId))
        : selectAgreementIds([...selectedAgreementIds, agreementId]),
    [selectedAgreementIds]
  );

  const { productGroupHierarchy, productGroups } = useFilterOptions();

  const { filters, setFilters, resetFilters } = useFilters([
    'productGroupModal',
    'searchQueryModal',
  ]);
  const selectedGroupNumber = useMemo(
    () => (filters.productGroupModal ? filters.productGroupModal[0] : null),
    [filters.productGroupModal]
  );
  const selectedProductGroup = useMemo(
    () => productGroups.find((pg) => pg.groupNumber === selectedGroupNumber),
    [selectedGroupNumber, productGroups]
  );

  const handleClose = useCallback(() => {
    onClose();
    resetFilters();
  }, [onClose, resetFilters]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiPaper-root': { minHeight: '90%', maxHeight: '90%', px: 0 } }}
    >
      <DialogTitle sx={{ pl: 3 }}>{t('calculus.actions.comparePriceLists')}</DialogTitle>
      <DialogContent className="padding-0" sx={{ display: 'flex', flexDirection: 'row' }}>
        <SuspenseWithSentryErrorBoundary>
          <PriceListTableContainer
            assortmentId={assortmentId}
            selectedAgreementIds={selectedAgreementIds}
            selectedAgreements={agreements.filter((agreement) =>
              selectedAgreementIds.includes(agreement.id)
            )}
            selectedProductGroup={selectedProductGroup?.groupNumber ?? levelProductGroupNr}
            checkedPriceType={checkedPriceType}
            query={filters.searchQueryModal ?? ''}
          />
        </SuspenseWithSentryErrorBoundary>

        <PriceListFilters
          agreements={agreements}
          productGroupHierarchy={productGroupHierarchy}
          selectedAgreementIds={selectedAgreementIds}
          selectedProductGroup={selectedProductGroup ?? undefined}
          checkedPriceType={checkedPriceType}
          selectAgreement={handleSelectAgreementId}
          selectProductGroup={(groupNumber: string | null) =>
            setFilters({ productGroupModal: groupNumber ? [groupNumber] : undefined })
          }
          setQuery={(query: string) => setFilters({ searchQueryModal: query })}
          handleCheckPriceType={checkPriceType}
        />
      </DialogContent>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(PriceComparisonDialog);
